import {Transaction} from "../../domain/transaction.entity";
import {
  TransactionType,
  TransactionStatus,
  PaginationMeta,
} from "../../domain/transaction.types";
import {
  V2LocalTransactionResponse,
  V2ForeignTransactionResponse,
  V2TransactionResponse,
  V2PaginationMeta,
} from "./transaction.mapper.types";

type V2ApiStatus = "Active" | "Completed" | "Paid" | "Pending Conversion";

/**
 * Maps v2 API responses to Transaction domain entities.
 * Handles both local and foreign transaction formats.
 *
 * Key differences from v1:
 * - v2 uses camelCase field names (not snake_case)
 * - v2 FX transactions have transactionId (v1 didn't)
 * - v2 pagination uses hasNextPage/hasPrevPage booleans
 *
 * Pure TypeScript - no React imports.
 */
export const TransactionMapperV2 = {
  /**
   * Maps a single v2 API response to a Transaction entity.
   */
  toDomain(response: V2TransactionResponse, type: TransactionType): Transaction {
    if (type === "local") {
      return this.mapLocalTransaction(response as V2LocalTransactionResponse);
    }
    return this.mapForeignTransaction(response as V2ForeignTransactionResponse);
  },

  /**
   * Maps an array of v2 API responses to Transaction entities.
   */
  toDomainList(
    responses: V2LocalTransactionResponse[] | V2ForeignTransactionResponse[],
    type: TransactionType
  ): Transaction[] {
    return responses.map((response) => this.toDomain(response, type));
  },

  /**
   * Maps v2 pagination metadata to domain pagination.
   * v2 uses boolean flags (hasNextPage/hasPrevPage) instead of page numbers.
   * We need currentPage and perPage from the query params to calculate page numbers.
   */
  mapPaginationMeta(
    v2Meta: V2PaginationMeta & {currentPage: number; perPage: number}
  ): PaginationMeta {
    const totalPages = Math.ceil(v2Meta.total / v2Meta.perPage);

    return {
      currentPage: v2Meta.currentPage,
      totalPages,
      perPage: v2Meta.perPage,
      nextPage: v2Meta.hasNextPage ? v2Meta.currentPage + 1 : null,
      previousPage: v2Meta.hasPrevPage ? v2Meta.currentPage - 1 : null,
    };
  },

  /**
   * Maps v2 local transaction response to Transaction entity.
   */
  mapLocalTransaction(response: V2LocalTransactionResponse): Transaction {
    const status = this.mapStatus(response.statusName);

    return Transaction.create({
      id: response.transactionId,
      reference: response.reference,
      type: "local",
      customerName: response.fullName ?? "",
      customerEmail: response.email ?? "",
      serviceName: response.serviceName,
      offeringType: response.offeringType,
      amount: response.amount,
      currency: response.currency,
      status,
      paymentDate: response.paymentDate,
      sourceTable: response.sourceTable,
      isDiscounted: response.isDiscounted,
      charges: response.charges,
      chargeBearer: response.chargeBearer,
      discountCode: response.discountCode,
    });
  },

  /**
   * Maps v2 foreign transaction response to Transaction entity.
   */
  mapForeignTransaction(response: V2ForeignTransactionResponse): Transaction {
    const status = this.mapStatus(response.statusName);

    // Parse price string which may include currency prefix (e.g., "USD 184.7")
    const amount = this.parsePrice(response.price);
    const currency = this.parseCurrency(response.price);

    return Transaction.create({
      id: response.transactionId,
      reference: response.reference,
      type: "foreign",
      customerName: response.customerName ?? "",
      customerEmail: response.customerEmail ?? "",
      serviceName: response.serviceName,
      offeringType: response.offeringType,
      amount,
      currency,
      status,
      paymentDate: response.paymentDate,
      exchangeRate: response.exchangeRate,
      convertedPrice: response.convertedPrice,
    });
  },

  /**
   * Maps v2 API status to domain TransactionStatus.
   */
  mapStatus(apiStatus: V2ApiStatus | undefined): TransactionStatus {
    if (!apiStatus) {
      return "PENDING";
    }

    switch (apiStatus) {
      case "Completed":
      case "Paid":
        return "COMPLETED";
      case "Active":
      case "Pending Conversion":
      default:
        return "PENDING";
    }
  },

  /**
   * Parses price string which may include currency prefix.
   * Examples: "USD 184.7" -> 184.7, "250.50" -> 250.5
   */
  parsePrice(price: string): number {
    const numericPart = price.replace(/[^\d.]/g, "");
    return parseFloat(numericPart) || 0;
  },

  /**
   * Extracts currency code from price string.
   * Examples: "USD 184.7" -> "USD", "250.50" -> ""
   */
  parseCurrency(price: string): string {
    const match = price.match(/^([A-Z]{2,4})\s/);
    return match ? match[1] : "";
  },
};
